import { BarChart, LineChart } from "@mantine/charts";
import { Alert, Card, Center, Loader, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";

import { useGlobalUsage, useStatsOverview } from "@/lib/admin/hooks";
import { fillDailyBuckets, getLast30DayRange } from "@/lib/admin/usage";

function formatErrorRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

type StatCardProps = {
  title: string;
  value: string;
  subtitle?: string;
};

function StatCard({ title, value, subtitle }: StatCardProps) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap={4}>
        <Text size="sm" c="dimmed">
          {title}
        </Text>
        <Text size="xl" fw={700}>
          {value}
        </Text>
        {subtitle ? (
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

/** Admin dashboard: aggregate metrics and usage charts (Epic 6 Task 6.5). */
export function DashboardPage() {
  const usageRange = useMemo(() => getLast30DayRange(), []);

  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    error: overviewError,
  } = useStatsOverview();

  const {
    data: usageData,
    isLoading: isUsageLoading,
    isError: isUsageError,
    error: usageError,
  } = useGlobalUsage(usageRange.fromISO, usageRange.toISO);

  const chartData = useMemo(() => {
    if (!usageData) {
      return [];
    }
    return fillDailyBuckets(usageData.items, usageRange.fromISO, usageRange.toISO);
  }, [usageData, usageRange.fromISO, usageRange.toISO]);

  const isUsageEmpty =
    !isUsageLoading &&
    !isUsageError &&
    chartData.every((row) => row.requests === 0 && row.errors === 0);

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>Dashboard</Title>
        <Text c="dimmed" size="sm">
          Platform usage and user metrics.
        </Text>
        {overview ? (
          <Text c="dimmed" size="xs">
            As of {new Date(overview.as_of).toLocaleString()}
          </Text>
        ) : null}
      </Stack>

      {isOverviewError ? (
        <Alert color="red" title="Failed to load dashboard metrics">
          {overviewError instanceof Error ? overviewError.message : "An unexpected error occurred."}
        </Alert>
      ) : null}

      {isOverviewLoading ? (
        <Center py="md">
          <Loader />
        </Center>
      ) : null}

      {!isOverviewLoading && !isOverviewError && overview ? (
        <>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <StatCard title="Total users" value={formatCount(overview.users.total)} />
            <StatCard
              title="Requests today"
              value={formatCount(overview.requests.today)}
              subtitle={`${formatCount(overview.requests.this_month)} this month`}
            />
            <StatCard
              title="Error rate today"
              value={formatErrorRate(overview.requests.error_rate_today)}
            />
          </SimpleGrid>

          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <Title order={4}>Top endpoints</Title>
              {overview.top_endpoints.length === 0 ? (
                <Text c="dimmed" ta="center" py="md">
                  No endpoint traffic recorded yet.
                </Text>
              ) : (
                <BarChart
                  h={Math.max(200, overview.top_endpoints.length * 36)}
                  data={overview.top_endpoints}
                  dataKey="endpoint"
                  series={[{ name: "count", color: "teal.6", label: "Requests" }]}
                  orientation="horizontal"
                  withTooltip
                />
              )}
            </Stack>
          </Card>
        </>
      ) : null}

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Title order={4}>Requests over time (last 30 days)</Title>
          {isUsageLoading ? (
            <Center py="md">
              <Loader />
            </Center>
          ) : null}
          {isUsageError ? (
            <Alert color="red" title="Failed to load usage">
              {usageError instanceof Error ? usageError.message : "An unexpected error occurred."}
            </Alert>
          ) : null}
          {!isUsageLoading && !isUsageError && isUsageEmpty ? (
            <Text c="dimmed" ta="center" py="md">
              No usage recorded in this period.
            </Text>
          ) : null}
          {!isUsageLoading && !isUsageError && !isUsageEmpty ? (
            <LineChart
              h={280}
              data={chartData}
              dataKey="date"
              series={[
                { name: "requests", color: "blue.6", label: "Requests" },
                { name: "errors", color: "red.6", label: "Errors" },
              ]}
              curveType="linear"
              withLegend
              withTooltip
            />
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
